import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import DefaultLayout from '@/layouts/Default.vue'
import AuthLayout from '@/layouts/Auth.vue'
import A4Landscape from '@/layouts/A4Landscape.vue'
import ServiceLayout from '@/layouts/Service.vue'

import NewDefaultLayout from '@/layouts/NewDefault.vue'
import OktellLayout from '@/layouts/Oktell.vue'

import AuthView from '@/views/Auth.vue'

import useAccount from '@/common/composable/useAccount'
import useAuth from '@/common/composable/useAuth'

import { createToast } from 'mosha-vue-toastify'

const routes: Array<RouteRecordRaw> = [
    {
        path: '/',
        name: 'DefaultLayout',
        component: DefaultLayout,
        children: [
            {
                path: '/nearby-localities-finder',
                name: 'NearbyLocalitiesFinder',
                component: () =>
                    import(
                        /* webpackChunkName: "nearby-localities-finder" */ '@/views/NearbyLocalitiesFinder.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['all-root-permission'],
                },
            },
            {
                path: '/cases',
                name: 'Cases',
                component: () => import(/* webpackChunkName: "cases" */ '@/views/Cases/Cases.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['crm.cases-admin.index', 'all-root-permission']
                },
            },
            {
                path: '/cases/create',
                name: 'CasesCreate',
                component: () =>
                    import(/* webpackChunkName: "cases-create" */ '@/views/Cases/CreateCase.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['crm.cases-admin.create', 'all-root-permission']
                },
            },
            {
                path: '/cases/update/:case_id',
                name: 'CasesUpdate',
                component: () =>
                    import(/* webpackChunkName: "cases-update" */ '@/views/Cases/UpdateCase.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['crm.cases-admin.update', 'all-root-permission']
                },
            },
            {
                path: '/blog',
                name: 'Blog',
                component: () => import(/* webpackChunkName: "blog" */ '@/views/Blog/Articles.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/blog/create',
                name: 'BlogCreate',
                component: () =>
                    import(/* webpackChunkName: "blog-create" */ '@/views/Blog/CreateArticle.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/blog/update/:article_id',
                name: 'BlogUpdate',
                component: () =>
                    import(/* webpackChunkName: "blog-update" */ '@/views/Blog/UpdateArticle.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/calculator',
                name: 'Calculator',
                component: () =>
                    import(/* webpackChunkName: "calculator" */ '@/views/Calculator.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/security-check',
                name: 'SecurityCheck',
                component: () =>
                    import(/* webpackChunkName: "security-check" */ '@/views/SecurityCheck.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['contract-adata-car-create-bid', 'all-root-permission'],
                },
            },
            {
                path: '/proposals',
                name: 'Proposals',
                component: () =>
                    import(/* webpackChunkName: "proposals" */ '@/views/Proposals/Proposals.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/merchants',
                name: 'Merchants',
                component: () =>
                    import(/* webpackChunkName: "merchants" */ '@/views/Merchants/Merchants.vue'),
                meta: {
                    requiresAuth: true,
                },
            },

            {
                path: '/flight-departures',
                name: 'FlightDepartures',
                component: () =>
                    import(
                        /* webpackChunkName: "flight-departures" */ '@/views/FlightDepartures/FlightDepartures.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/navigation',
                name: 'Navigation',
                component: () =>
                    import(/* webpackChunkName: "navigation" */ '@/views/Navigation.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/update-ftl-order/:order_id',
                name: 'FTLOrderUpdate',
                component: () =>
                    import(/* webpackChunkName: "update-ftl-order" */ '@/views/FTLOrderUpdate.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['order-ftl-update', 'all-root-permission'],
                },
            },
            {
                path: '/surveys',
                name: 'Surveys',
                component: () =>
                    import(/* webpackChunkName: "surveys" */ '@/views/Surveys/Surveys.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/surveys-create',
                name: 'SurveysCreate',
                component: () =>
                    import(
                        /* webpackChunkName: "surveys-create" */ '@/views/Surveys/CreateSurvey.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/surveys-update/:survey_id',
                name: 'SurveysUpdate',
                component: () =>
                    import(
                        /* webpackChunkName: "survey-update" */ '@/views/Surveys/UpdateSurvey.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/surveys/:survey_id',
                name: 'SurveyId',
                component: () =>
                    import(/* webpackChunkName: "survey" */ '@/views/Surveys/SurveyId.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/',
                name: 'Home',
                component: () => import(/* webpackChunkName: "home" */ '@/views/Home/Home.vue'),
                children: [
                    {
                        path: '',
                        name: 'InvoicesHistory',
                        component: () =>
                            import(
                                /* webpackChunkName: "invoiceshistory" */ '@/views/Home/Invoices.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/ftl',
                        name: 'OrdersHistory',
                        component: () =>
                            import(
                                /* webpackChunkName: "orders-history" */ '@/views/Home/Orders/Orders.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                        children: [
                            {
                                path: '',
                                name: 'FTLOrders',
                                component: () =>
                                    import(
                                        /* webpackChunkName: "ftl-orders" */ '@/views/Home/Orders/FTLOrders.vue'
                                    ),
                                meta: {
                                    requiresAuth: true,
                                },
                            },
                            {
                                path: 'bids',
                                name: 'FTLBids',
                                component: () =>
                                    import(
                                        /* webpackChunkName: "ftl-bids" */ '@/views/FTLBids/FTLBids.vue'
                                    ),
                                meta: {
                                    requiresAuth: true,
                                },
                            },
                            {
                                path: 'intracity',
                                name: 'FtlIntracityList',
                                component: () =>
                                    import(
                                        /* webpackChunkName: "ftl-intracity-list" */ '@/views/FTLBids/FtlIntracityList.vue'
                                    ),
                                meta: {
                                    requiresAuth: true,
                                    permissions: ['cabinet_ftl_intracity_index'],
                                },
                            },
                            {
                                path: '/ftl/bids/:id',
                                name: 'FTLBid',
                                component: () =>
                                    import(/* webpackChunkName: "ftl-bid" */ '@/views/FTLBid.vue'),
                                meta: {
                                    requiresAuth: true,
                                },
                            },
                            {
                                path: '/ftl/bids/:id/edit',
                                name: 'FTLBidEdit',
                                component: () =>
                                    import(
                                        /* webpackChunkName: "ftl-bid-edit" */ '@/views/FTLBidEdit.vue'
                                    ),
                                meta: {
                                    requiresAuth: true,
                                },
                            },
                            {
                                path: '/ftl/bids/:id/create',
                                name: 'CreateFTLOrder',
                                component: () =>
                                    import(
                                        /* webpackChunkName: "create-ftl-order" */ '@/views/CreateOrder/FtlOrder.vue'
                                    ),
                                meta: {
                                    requiresAuth: true,
                                },
                            },
                        ],
                    },
                    {
                        path: '/express',
                        name: 'ExpressOrders',
                        component: () =>
                            import(
                                /* webpackChunkName: "express-orders" */ '@/views/Home/ExpressOrders.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: [
                                'delivery-raketa-admin-order-index',
                                'all-root-permission',
                            ],
                        },
                    },
                    {
                        path: 'with-services',
                        name: 'WithServices',
                        component: () =>
                            import(
                                /* webpackChunkName: "with-services" */ '@/views/Home/WithServices.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                ],
            },
            {
                path: '/invoices/import',
                name: 'InvoicesImport',
                component: () =>
                    import(
                        /* webpackChunkName: "invoices-import" */ '@/views/Home/InvoicesImport.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['order-logistics-info-import-cash-sum', 'all-root-permission'],
                },
            },
            {
                path: '/invoices/cancel',
                name: 'InvoicesCancel',
                component: () =>
                    import(
                        /* webpackChunkName: "invoices-cancel" */ '@/views/Home/InvoicesCancel.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/invoices/delete-additional',
                name: 'InvoicesDeleteAdditional',
                component: () =>
                    import(
                        /* webpackChunkName: "invoices-delete-additional" */ '@/views/Home/InvoicesDeleteAdditional.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/invoices/price-import',
                name: 'InvoicesPriceImport',
                component: () =>
                    import(
                        /* webpackChunkName: "invoices-price-import" */ '@/views/Home/InvoicesPriceImport.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: [
                        'order-logistics-info-import-declared-price',
                        'all-root-permission',
                    ],
                },
            },
            {
                path: '/invoices/sync',
                name: 'SyncOnec',
                component: () =>
                    import(/* webpackChunkName: "sync-onec" */ '@/views/OneC/SyncOnec.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/order/:order_id',
                name: 'Order',
                component: () => import(/* webpackChunkName: "order" */ '@/views/Order.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/order/:order_id/update-services',
                name: 'OrderUpdateServices',
                component: () =>
                    import(
                        /* webpackChunkName: "order-update-services" */ '@/views/OrderAdditionalServicesUpdate.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/ftl-orders/:order_id',
                name: 'FTLOrder',
                component: () => import(/* webpackChunkName: "ftl-order" */ '@/views/FTLOrder.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/ftl/intracity/:order_id',
                name: 'FTLIntracity',
                component: () =>
                    import(/* webpackChunkName: "ftl-intracity" */ '@/views/FTLIntracity.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/ftl/intracity/:order_id/edit',
                name: 'FTLIntracityEdit',
                component: () =>
                    import(
                        /* webpackChunkName: "ftl-intracity-edit" */ '@/views/FTLIntracityEdit.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/ftl/bids/:id',
                name: 'FTLBid',
                component: () => import(/* webpackChunkName: "ftl-bid" */ '@/views/FTLBid.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/order/:order_id/edit',
                name: 'OrderEdit',
                component: () =>
                    import(/* webpackChunkName: "order-edit" */ '@/views/OrderEdit.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/order/create',
                name: 'CreateOrder',
                component: () =>
                    import(/* webpackChunkName: "create-order" */ '@/views/CreateOrder/index.vue'),
                children: [
                    {
                        path: '',
                        name: 'CreateLTLOrder',
                        component: () =>
                            import(
                                /* webpackChunkName: "create-ltl-order" */ '@/views/CreateOrder/LTL.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'express',
                        name: 'CreateExpressOrder',
                        component: () =>
                            import(
                                /* webpackChunkName: "create-express-order" */ '@/views/CreateOrder/Express.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: ['delivery-raketa-orders-store', 'all-root-permission'],
                        },
                    },
                    {
                        path: 'ftl-bids',
                        name: 'CreateFTLBid',
                        component: () =>
                            import(
                                /* webpackChunkName: "create-ftl-holder" */ '@/views/CreateOrder/FtlHolder.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                        children: [
                            {
                                path: '',
                                name: 'CreateFTLBid',
                                component: () =>
                                    import(
                                        /* webpackChunkName: "create-ftl-bid" */ '@/views/CreateFTLBid/NewFtlBid.vue'
                                    ),
                                meta: {
                                    requiresAuth: true,
                                },
                            },
                            {
                                path: 'intracity',
                                name: 'CreateFTLBidIntracity',
                                component: () =>
                                    import(
                                        /* webpackChunkName: "create-ftl-bid-intracity" */ '@/views/CreateFTLBid/NewFtlBidIntracity.vue'
                                    ),
                                meta: {
                                    requiresAuth: true,
                                    permissions: ['cabinet-ftl-intracity-order-create'],
                                },
                            },
                        ],
                    },
                    {
                        path: 'spark',
                        name: 'CreateSparkOrder',
                        component: () =>
                            import(
                                /* webpackChunkName: "create-spark-order" */ '@/views/CreateOrder/Spark.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                ],
            },
            {
                path: '/invoice/:invoice_id',
                name: 'Invoice',
                component: () => import(/* webpackChunkName: "invoice" */ '@/views/Invoice.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/invoice/:invoice_id/update-services',
                name: 'InvoiceUpdateServices',
                component: () =>
                    import(
                        /* webpackChunkName: "invoice-update-services" */ '@/views/InvoiceAdditionalServicesUpdate.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/express/:order_id',
                name: 'ExpressOrder',
                component: () =>
                    import(/* webpackChunkName: "express-order" */ '@/views/ExpressOrder.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['delivery-raketa-orders-index', 'all-root-permission'],
                },
            },
            {
                path: '/invoice/:invoice_id/edit',
                name: 'InvoiceEdit',
                component: () =>
                    import(/* webpackChunkName: "invoice-edit" */ '@/views/InvoiceEdit.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/users',
                name: 'Users',
                component: () => import(/* webpackChunkName: "users" */ '@/views/Users/Users.vue'),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'UsersList',
                        component: () =>
                            import(
                                /* webpackChunkName: "users-list" */ '@/views/Users/UsersList.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/employees',
                        name: 'EmployeesList',
                        component: () =>
                            import(
                                /* webpackChunkName: "employees-list" */ '@/views/Users/EmployeesList.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                ],
            },
            {
                path: '/companies',
                name: 'Companies',
                component: () =>
                    import(
                        /* webpackChunkName: "companies" */ '@/views/Companies/CompaniesContainer.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'BusinessCompanies',
                        component: () =>
                            import(
                                /* webpackChunkName: "companies-business" */ '@/views/Companies/Companies.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'spark-kz',
                        name: 'SparkKzCompanies',
                        component: () =>
                            import(
                                /* webpackChunkName: "companies-sparkkz" */ '@/views/Companies/CompaniesSparkKz.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: ['call-center-companies-index', 'all-root-permission'],
                        },
                    },
                    {
                        path: 'spark-kz/create',
                        name: 'CreateSparkKzCompany',
                        component: () =>
                            import(
                                /* webpackChunkName: "create-sparkkz-company" */ '@/views/Companies/CreateSparkKzCompany.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: ['call-center-companies-store', 'all-root-permission'],
                        },
                    },
                    {
                        path: 'spark-kz/update/:company_id',
                        name: 'UpdateSparkKzCompany',
                        component: () =>
                            import(
                                /* webpackChunkName: "update-sparkkz-company" */ '@/views/Companies/UpdateSparkKzCompany.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: ['call-center-companies-update', 'all-root-permission'],
                        },
                    },
                ],
            },
            {
                path: '/companies/:company_id',
                name: 'CompanyId',
                component: () =>
                    import(/* webpackChunkName: "company-id" */ '@/views/CompanyId.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/directions',
                name: 'Directions',
                component: () =>
                    import(/* webpackChunkName: "directions" */ '@/views/Directions.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/directions/import',
                name: 'DirectionsImport',
                component: () =>
                    import(
                        /* webpackChunkName: "directions-import" */ '@/views/DirectionsImport.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/stories',
                name: 'Stories',
                component: () =>
                    import(/* webpackChunkName: "stories" */ '@/views/Stories/index.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/stories/:story_id',
                name: 'StoryId',
                component: () =>
                    import(/* webpackChunkName: "stories-id" */ '@/views/Stories/StoryId.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/contracts',
                name: 'Contracts',
                component: () =>
                    import(/* webpackChunkName: "contracts" */ '@/views/Contracts/Contracts.vue'),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'ContractsList',
                        component: () =>
                            import(
                                /* webpackChunkName: "contracts-list" */ '@/views/Contracts/ContractsList.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                        children: [
                            {
                                path: '',
                                name: 'ContractsListHolder',
                                component: () =>
                                    import(
                                        /* webpackChunkName: "contracts-list-holder" */ '@/views/Contracts/ContractsListHolder.vue'
                                    ),
                                meta: {
                                    requiresAuth: true,
                                },
                                children: [
                                    {
                                        path: '',
                                        name: 'AssignedContracts',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "assigned-contracts" */ '@/views/Contracts/AssignedContracts.vue'
                                            ),
                                        meta: {
                                            requiresAuth: true,
                                        },
                                    },
                                    {
                                        path: 'to-be-agreed',
                                        name: 'ToBeAgreedContracts',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "to-be-aggreed-contracts" */ '@/views/Contracts/ToBeAgreed.vue'
                                            ),
                                        meta: {
                                            permissions: ['dogovor24-index', 'all-root-permission'],
                                        },
                                    },
                                    {
                                        path: 'register',
                                        name: 'RegisterContracts',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "register-contracts" */ '@/views/Contracts/RegisterContracts.vue'
                                            ),
                                        meta: {
                                            requiresAuth: true,
                                            permissions: [
                                                'contract-register-index',
                                                'all-root-permission',
                                            ],
                                        },
                                    },
                                    {
                                        path: 'archive',
                                        name: 'ArchiveContracts',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "archive-contracts" */ '@/views/Contracts/ArchiveContracts.vue'
                                            ),
                                        meta: {
                                            requiresAuth: true,
                                            permissions: [
                                                'contract-contract-archive',
                                                'all-root-permission',
                                            ],
                                        },
                                    },
                                    //
                                    {
                                        path: '/contracts/bids',
                                        name: 'Bids',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "bids" */ '@/views/Contracts/Bids.vue'
                                            ),
                                        meta: {
                                            requiresAuth: true,
                                        },
                                        children: [
                                            {
                                                path: '',
                                                name: 'BidsList',
                                                component: () =>
                                                    import(
                                                        /* webpackChunkName: "bids-list" */ '@/views/Contracts/BidsList.vue'
                                                    ),
                                                meta: {
                                                    requiresAuth: true,
                                                },
                                            },
                                            {
                                                path: 'wait-list',
                                                name: 'BidsWaitList',
                                                component: () =>
                                                    import(
                                                        /* webpackChunkName: "bids-wait-list" */ '@/views/Contracts/BidsWaitList.vue'
                                                    ),
                                                meta: {
                                                    requiresAuth: true,
                                                },
                                            },
                                            {
                                                path: 'new',
                                                name: 'BidsListNew',
                                                component: () =>
                                                    import(
                                                        /* webpackChunkName: "bids-list-new" */ '@/views/Contracts/BidsListNew.vue'
                                                    ),
                                                meta: {
                                                    requiresAuth: true,
                                                },
                                            },
                                        ],
                                    },
                                    {
                                        path: '/contracts/templates',
                                        name: 'Templates',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "templates" */ '@/views/Contracts/TemplatesList.vue'
                                            ),
                                        meta: {
                                            requiresAuth: true,
                                        },
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: '/bids/:id',
                        name: 'Bid',
                        component: () => import(/* webpackChunkName: "bid" */ '@/views/Bid.vue'),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/bids/new/:id',
                        name: 'BidNew',
                        component: () =>
                            import(/* webpackChunkName: "bid-new" */ '@/views/BidNew.vue'),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                ],
            },
            {
                path: '/contracts/:contract_id',
                name: 'ContractId',
                component: () =>
                    import(
                        /* webpackChunkName: "contracts-id" */ '@/views/Contracts/ContractId.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/contracts/register/:contract_id',
                name: 'ContractRegisterId',
                component: () =>
                    import(
                        /* webpackChunkName: "contracts-register-id" */ '@/views/Contracts/ContractRegisterId.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/warehouses/:id',
                name: 'WarehouseId',
                component: () =>
                    import(
                        /* webpackChunkName: "warehouse-id" */ '@/views/Warehouses/WarehouseId.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/warehouses/:id/edit',
                name: 'WarehouseEdit',
                component: () =>
                    import(
                        /* webpackChunkName: "warehouse-edit" */ '@/views/Warehouses/WarehouseEdit.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/contracts/register/edit/:contract_id',
                name: 'ContractRegisterEdit',
                component: () =>
                    import(
                        /* webpackChunkName: "contracts-register-edit" */ '@/views/Contracts/ContractRegisterEdit.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/calls',
                name: 'Calls',
                component: () => import(/* webpackChunkName: "calls" */ '@/views/Calls/Calls.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/pretensions',
                name: 'Pretensions',
                component: () =>
                    import(
                        /* webpackChunkName: "pretensions" */ '@/views/Pretensions/Pretensions.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'PretensionsList',
                        component: () =>
                            import(
                                /* webpackChunkName: "pretensions-list" */ '@/views/Pretensions/PretensionsList.vue'
                            ),
                    },
                    {
                        path: 'complaints',
                        name: 'Complaints',
                        component: () =>
                            import(
                                /* webpackChunkName: "complaints" */ '@/views/Pretensions/Complaints.vue'
                            ),
                    },
                ],
            },
            {
                path: '/pretensions/:id',
                name: 'PretensionId',
                component: () =>
                    import(
                        /* webpackChunkName: "pretension-id" */ '@/views/Pretensions/PretensionId.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/complaints/:id',
                name: 'ComplaintId',
                component: () =>
                    import(
                        /* webpackChunkName: "complaint-id" */ '@/views/Pretensions/ComplaintId.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/gateway',
                name: 'Gateway',
                component: () =>
                    import(/* webpackChunkName: "gateway" */ '@/views/Gateway/Gateway.vue'),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'GatewayClients',
                        component: () =>
                            import(
                                /* webpackChunkName: "gatewayclients" */ '@/views/Gateway/Clients.vue'
                            ),
                    },
                    {
                        path: 'roles',
                        name: 'GatewayRoles',
                        component: () =>
                            import(
                                /* webpackChunkName: "gatewayroles" */ '@/views/Gateway/Roles.vue'
                            ),
                    },
                    {
                        path: 'permissions',
                        name: 'GatewayPermissions',
                        component: () =>
                            import(
                                /* webpackChunkName: "gatewaypermissions" */ '@/views/Gateway/Permissions.vue'
                            ),
                    },
                    {
                        path: 'microservices',
                        name: 'GatewayMicroservices',
                        component: () =>
                            import(
                                /* webpackChunkName: "gatewaymicroservices" */ '@/views/Gateway/Microservices.vue'
                            ),
                    },
                    {
                        path: 'scopes',
                        name: 'GatewayScopes',
                        component: () =>
                            import(
                                /* webpackChunkName: "gatewayscopes" */ '@/views/Gateway/Scopes.vue'
                            ),
                    },
                    {
                        path: 'scope/:scope_id',
                        name: 'ScopeRoutes',
                        component: () =>
                            import(
                                /* webpackChunkName: "gatewayscoperoutes" */ '@/views/Gateway/ScopeRoutes.vue'
                            ),
                    },
                    {
                        path: 'routes',
                        name: 'GatewayRoutes',
                        component: () =>
                            import(
                                /* webpackChunkName: "gatewayroutes" */ '@/views/Gateway/Routes.vue'
                            ),
                    },
                ],
            },
            {
                path: '/tmc',
                name: 'TMC',
                component: () => import(/* webpackChunkName: "tmc" */ '@/views/TMC/TMC.vue'),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'TMCList',
                        component: () =>
                            import(/* webpackChunkName: "tmc-list" */ '@/views/TMC/TMCList.vue'),
                        meta: {
                            permissions: ['inventory-items-index', 'all-root-permission'],
                        },
                    },
                    {
                        path: 'admission',
                        name: 'Admissions',
                        component: () =>
                            import(
                                /* webpackChunkName: "admissions" */ '@/views/TMC/Admission.vue'
                            ),
                        meta: {
                            permissions: ['receipts-index', 'all-root-permission'],
                        },
                    },
                    {
                        path: 'relocations',
                        name: 'Relocations',
                        component: () =>
                            import(
                                /* webpackChunkName: "relocations" */ '@/views/TMC/Relocation.vue'
                            ),
                        meta: {
                            permissions: ['receipts-index', 'all-root-permission'],
                        },
                    },
                    {
                        path: 'remains',
                        name: 'Remains',
                        component: () =>
                            import(/* webpackChunkName: "remains" */ '@/views/TMC/Remains.vue'),
                        meta: {
                            permissions: [
                                'inventory-values-index',
                                'inventory-values-show',
                                'all-root-permission',
                            ],
                        },
                    },
                    {
                        path: 'write-offs',
                        name: 'WriteOffs',
                        component: () =>
                            import(/* webpackChunkName: "writeoffs" */ '@/views/TMC/WriteOffs.vue'),
                        meta: {
                            permissions: ['write-offs-index', 'all-root-permission'],
                        },
                    },
                    {
                        path: 'update',
                        name: 'UpdateExcel',
                        component: () =>
                            import(
                                /* webpackChunkName: "update-excel" */ '@/views/TMC/UpdateExcel.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                ],
            },
            {
                path: '/tmc/admission/:id',
                name: 'Admission',
                component: () =>
                    import(/* webpackChunkName: "admission-id" */ '@/views/TMC/AdmissionId.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['receipts-show', 'all-root-permission'],
                },
            },
            {
                path: '/tmc/relocation/:id',
                name: 'Relocation',
                component: () =>
                    import(/* webpackChunkName: "relocation-id" */ '@/views/TMC/RelocationId.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['receipts-show', 'all-root-permission'],
                },
            },
            {
                path: '/tmc/write-offs/:id',
                name: 'WriteoffId',
                component: () =>
                    import(/* webpackChunkName: "writeoff-id" */ '@/views/TMC/WriteOffId.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/tmc/write-offs/:id/update',
                name: 'WriteoffUpadte',
                component: () =>
                    import(
                        /* webpackChunkName: "writeoff-update" */ '@/views/TMC/UpdateWriteOff.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['write-offs-store', 'all-root-permission'],
                },
            },
            {
                path: '/tmc/write-offs/create',
                name: 'CreateWriteOff',
                component: () =>
                    import(
                        /* webpackChunkName: "create-writeoff" */ '@/views/TMC/CreateWriteOff.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['write-offs-store', 'all-root-permission'],
                },
            },
            {
                path: '/admission',
                name: 'AdmissionActions',
                component: () =>
                    import(
                        /* webpackChunkName: "admission-actions" */ '@/views/TMC/Admissions/Admissions.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: 'create',
                        name: 'AdmissionsCreate',
                        component: () =>
                            import(
                                /* webpackChunkName: "admissions-create" */ '@/views/TMC/Admissions/CreateAdmission.vue'
                            ),
                        meta: {
                            permissions: ['inventory-items-index', 'all-root-permission'],
                        },
                    },
                    {
                        path: 'secondary',
                        name: 'SecondaryMaterial',
                        component: () =>
                            import(
                                /* webpackChunkName: "secondary-material" */ '@/views/TMC/Admissions/SecondaryMaterial.vue'
                            ),
                        meta: {
                            permissions: [
                                'receipts-secondary-material-store',
                                'all-root-permission',
                            ],
                        },
                    },
                ],
            },
            {
                path: '/tmc/admission/:id/update',
                name: 'AdmissionUpdate',
                component: () =>
                    import(
                        /* webpackChunkName: "admission-update" */ '@/views/TMC/UpdateAdmission.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['inventory-items-store', 'all-root-permission'],
                },
            },
            {
                path: '/tmc/relocation/create',
                name: 'RelocationCreate',
                component: () =>
                    import(
                        /* webpackChunkName: "relocation-create" */ '@/views/TMC/CreateRelocation.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['inventory-items-store', 'all-root-permission'],
                },
            },
            {
                path: '/tmc/relocation/update/:relocation_id',
                name: 'RelocationUpdate',
                component: () =>
                    import(
                        /* webpackChunkName: "relocation-update" */ '@/views/TMC/UpdateRelocation.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['inventory-items-store', 'all-root-permission'],
                },
            },
            {
                path: '/tmc/create',
                name: 'TmcCreate',
                component: () =>
                    import(/* webpackChunkName: "tmc-create" */ '@/views/TMC/CreateTMC.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['inventory-items-store', 'all-root-permission'],
                },
            },
            {
                path: '/rp',
                name: 'RP',
                component: () => import(/* webpackChunkName: "rp" */ '@/views/RP/RP.vue'),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'RPDefault',
                        component: () =>
                            import(/* webpackChunkName: "rpdefault" */ '@/views/RP/RPDefault.vue'),
                    },
                    {
                        path: 'express',
                        name: 'RPExpress',
                        component: () =>
                            import(/* webpackChunkName: "rpexpress" */ '@/views/RP/RPExpress.vue'),
                    },
                ],
            },
            {
                path: '/dispatcher/delivered-invoices',
                name: 'DeliveredInvoices',
                component: () =>
                    import(
                        /* webpackChunkName: "delivered-invoices" */ '@/views/Dispatcher/DeliveredInvoicesList.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/dispatcher',
                name: 'Dispatcher',
                component: () =>
                    import(
                        /* webpackChunkName: "dispatcher" */ '@/views/Dispatcher/Dispatcher.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'DispatcherPanel',
                        component: () =>
                            import(
                                /* webpackChunkName: "dispatcherpanel" */ '@/views/Dispatcher/DispatcherPanel.vue'
                            ),
                    },
                    {
                        path: 'without-address',
                        name: 'DispatcherWithOutAddress',
                        component: () =>
                            import(
                                /* webpackChunkName: "dispatcherwithoutaddress" */ '@/views/Dispatcher/DispatcherWithOutAddress.vue'
                            ),
                    },
                    // {
                    //     path: 'sectors',
                    //     name: 'Sectors',
                    //     component: () =>
                    //         import(
                    //             /* webpackChunkName: "sectors" */ '@/views/Dispatcher/Sectors.vue'
                    //         ),
                    // },
                ],
            },
            {
                path: '/dispatcher/lists',
                name: 'Lists',
                component: () =>
                    import(
                        /* webpackChunkName: "dispatcherlists" */ '@/views/Dispatcher/DispatcherLists.vue'
                    ),
                children: [
                    {
                        path: 'takes',
                        name: 'Takes',
                        component: () =>
                            import(
                                /* webpackChunkName: "dispatchertakes" */ '@/views/Dispatcher/Takes.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'delivery',
                        name: 'Delivery',
                        component: () =>
                            import(
                                /* webpackChunkName: "dispatcherdelivery" */ '@/views/Dispatcher/Delivery.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'couriers',
                        name: 'DispatcherCouriers',
                        component: () =>
                            import(
                                /* webpackChunkName: "dispatchercouriers" */ '@/views/Dispatcher/Couriers.vue'
                            ),
                        meta: { requiresAuth: true },
                    },
                ],
            },
            {
                path: '/couriers',
                name: 'Couriers',
                component: () =>
                    import(/* webpackChunkName: "couriers" */ '@/views/Couriers/Couriers.vue'),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'CouriersList',
                        component: () =>
                            import(
                                /* webpackChunkName: "courierslist" */ '@/views/Couriers/CouriersList.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'cars',
                        name: 'CourierCars',
                        component: () =>
                            import(
                                /* webpackChunkName: "couriers-cars" */ '@/views/Couriers/Cars.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: ['cabinet-courier-car-index', 'all-root-permission'],
                        },
                    },
                    {
                        path: 'carriers',
                        name: 'CarriersList',
                        component: () =>
                            import(
                                /* webpackChunkName: "carrierslist" */ '@/views/Couriers/CarriersList.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'categories',
                        name: 'CouriersCategories',
                        component: () =>
                            import(
                                /* webpackChunkName: "couriersCategories" */ '@/views/Couriers/CouriersCategories.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                ],
            },
            {
                path: '/couriers/carriers/:id',
                name: 'CarrierId',
                component: () =>
                    import(/* webpackChunkName: "carrier-id" */ '@/views/Couriers/CarrierId.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/couriers/:id',
                name: 'CouriersId',
                component: () =>
                    import(/* webpackChunkName: "couriers-id" */ '@/views/Couriers/CourierId.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/couriers/create-car',
                name: 'CreateCar',
                component: () =>
                    import(/* webpackChunkName: "create-car" */ '@/views/Couriers/CreateCar.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['cabinet-courier-car-create', 'all-root-permission'],
                },
            },
            {
                path: '/couriers/cars/:car_id/update',
                name: 'UpdateCar',
                component: () =>
                    import(/* webpackChunkName: "update-car" */ '@/views/Couriers/UpdateCar.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['cabinet-courier-car-create', 'all-root-permission'],
                },
            },
            {
                path: '/couriers/cars/:car_id',
                name: 'CarId',
                component: () =>
                    import(/* webpackChunkName: "car-id" */ '@/views/Couriers/CarId.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/pvz',
                name: 'PVZ',
                component: () => import(/* webpackChunkName: "pvz" */ '@/views/PVZ/PVZ.vue'),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'PVZList',
                        component: () =>
                            import(/* webpackChunkName: "pvzlist" */ '@/views/PVZ/PVZList.vue'),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'invoices',
                        name: 'InvoicesList',
                        component: () =>
                            import(
                                /* webpackChunkName: "invoiceslist" */ '@/views/PVZ/InvoicesList.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                ],
            },
            {
                path: '/changes-history',
                name: 'ChangesHistory',
                component: () =>
                    import(
                        /* webpackChunkName: "changes-history" */ '@/views/ChangesHistory/ChangesHistoryList.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/kpi',
                name: 'KPI',
                component: () => import(/* webpackChunkName: "kpi" */ '@/views/KPI/KPI.vue'),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'KPIDelivery',
                        component: () =>
                            import(
                                /* webpackChunkName: "kpidelivery" */ '@/views/KPI/KPIDelivery.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'telephony',
                        name: 'KPITelephony',
                        component: () =>
                            import(
                                /* webpackChunkName: "kpitelephony" */ '@/views/KPI/KPITelephony.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'report',
                        name: 'KPIReport',
                        component: () =>
                            import(/* webpackChunkName: "kpireport" */ '@/views/KPI/KPIReport.vue'),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                ],
            },
            {
                path: '/one_c',
                name: 'OneCHolder',
                component: () =>
                    import(/* webpackChunkName: "one-c-holder" */ '@/views/OneC/Holder.vue'),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'OneC',
                        component: () =>
                            import(/* webpackChunkName: "one-c" */ '@/views/OneC/OneC.vue'),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'resend',
                        name: 'ResendDispatcher',
                        component: () =>
                            import(
                                /* webpackChunkName: "resend-dispatcher" */ '@/views/OneC/ResendDispatcher.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'sdt',
                        name: 'SdtOrders',
                        component: () =>
                            import(
                                /* webpackChunkName: "sdt-orders" */ '@/views/OneC/SdtOrders.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'delivery',
                        name: 'ResendDelivery',
                        component: () =>
                            import(
                                /* webpackChunkName: "resend-delivery" */ '@/views/OneC/ResendDelivery.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                ],
            },
            {
                path: '/sla-import',
                name: 'SLAImport',
                component: () =>
                    import(/* webpackChunkName: "sla-import" */ '@/views/SlaImport.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['location.sla-import', 'all-root-permission'],
                },
            },
            {
                path: '/sla',
                name: 'Sla',
                component: () => import(/* webpackChunkName: "sla" */ '@/views/Sla/Sla.vue'),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'SlaLtl',
                        component: () =>
                            import(/* webpackChunkName: "sla-ltl" */ '@/views/Sla/SlaLtl.vue'),
                    },
                    {
                        path: 'ftl',
                        name: 'SlaFtl',
                        component: () =>
                            import(/* webpackChunkName: "sla-ftl" */ '@/views/Sla/SlaFtl.vue'),
                    },
                ],
            },
            {
                path: '/comments',
                name: 'Comments',
                component: () => import(/* webpackChunkName: "comments" */ '@/views/Comments.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/tariffs-express',
                name: 'TariffsExpress',
                component: () =>
                    import(/* webpackChunkName: "tariffs-express" */ '@/views/TariffsExpress.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/settings',
                name: 'Settings',
                component: () =>
                    import(/* webpackChunkName: "settings" */ '@/views/Settings/Settings.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['cabinet-settings-index', 'all-root-permission'],
                },
                children: [
                    {
                        path: 'cabinet',
                        name: 'SettingsCabinet',
                        component: () =>
                            import(
                                /* webpackChunkName: "cabinet-settings" */ '@/views/Settings/Cabinet.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: ['cabinet-settings-index', 'all-root-permission'],
                        },
                    },
                    {
                        path: 'notifications',
                        name: 'SettingsNotifications',
                        component: () =>
                            import(
                                /* webpackChunkName: "notifications-settings" */ '@/views/Settings/Notifications.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: ['notification-settings-index', 'all-root-permission'],
                        },
                    },
                    {
                        path: 'notifications-it',
                        name: 'SettingsNotificationsIt',
                        component: () =>
                            import(
                                /* webpackChunkName: "notifications-settings-it" */ '@/views/Settings/NotificationsIt.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: [
                                'notification.company-settings.index',
                                'all-root-permission',
                            ],
                        },
                    },
                    {
                        path: 'contract-types',
                        name: 'SettingsContractTypes',
                        component: () =>
                            import(
                                /* webpackChunkName: "settings-contract-types" */ '@/views/Settings/ContractTypes.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: ['contract-type-index', 'all-root-permission'],
                        },
                    },
                ],
            },
            {
                path: '/vacancies',
                name: 'Vacancies',
                component: () =>
                    import(/* webpackChunkName: "vacancies" */ '@/views/Vacancies/Vacancies.vue'),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'VacanciesList',
                        component: () =>
                            import(
                                /* webpackChunkName: "vacancieslist" */ '@/views/Vacancies/VacanciesList.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'vacancy/:vacancy_id',
                        name: 'VacancyId',
                        component: () =>
                            import(
                                /* webpackChunkName: "vacancyid" */ '@/views/Vacancies/VacancyId.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'categories',
                        name: 'VacanciesCategories',
                        component: () =>
                            import(
                                /* webpackChunkName: "vacanciescategories" */ '@/views/Vacancies/VacanciesCategories.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'regions',
                        name: 'VacanciesRegions',
                        component: () =>
                            import(
                                /* webpackChunkName: "vacanciesregions" */ '@/views/Vacancies/VacanciesRegions.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'feedbacks',
                        name: 'VacanciesFeedbacks',
                        component: () =>
                            import(
                                /* webpackChunkName: "vacanciesfeedbacks" */ '@/views/Vacancies/VacanciesFeedbacks.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'experience',
                        name: 'VacanciesExperience',
                        component: () =>
                            import(
                                /* webpackChunkName: "vacanciesexperience" */ '@/views/Vacancies/VacanciesExperience.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                ],
            },
            {
                path: '/invoice-scans',
                name: 'InvoiceScans',
                component: () =>
                    import(/* webpackChunkName: "invoicescans" */ '@/views/InvoiceScans.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/cities/create',
                name: 'CitiesCreate',
                component: () =>
                    import(/* webpackChunkName: "cities-create" */ '@/views/CityCreate.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/warehouses/create',
                name: 'WarehouseCreate',
                component: () =>
                    import(
                        /* webpackChunkName: "warehouse-create" */ '@/views/Warehouses/WarehouseCreate.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/cities/update/:city_id',
                name: 'CitiesUpdate',
                component: () =>
                    import(/* webpackChunkName: "cities-update" */ '@/views/CityUpdate.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/cities',
                name: 'Cities',
                component: () =>
                    import(/* webpackChunkName: "cities" */ '@/views/Cities/Cities.vue'),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'CitiesList',
                        component: () =>
                            import(
                                /* webpackChunkName: "citieslist" */ '@/views/Cities/CitiesList.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'regions',
                        name: 'RegionsList',
                        component: () =>
                            import(
                                /* webpackChunkName: "regionslist" */ '@/views/Cities/RegionsList.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                ],
            },
            {
                path: '/warehouses',
                name: 'Warehouses',
                component: () =>
                    import(
                        /* webpackChunkName: "warehouses" */ '@/views/Warehouses/Warehouses.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
                children: [],
            },
            {
                path: '/billing/transcripts/:transcript_id',
                name: 'BillingTranscript',
                component: () =>
                    import(
                        /* webpackChunkName: "transcript-id" */ '@/views/Billing/TranscriptId.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/billing/import',
                name: 'BillingImport',
                component: () =>
                    import(/* webpackChunkName: "billing-import" */ '@/views/Billing/Import.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/billing/transcripts/:id/update',
                name: 'BillingTranscriptUpdate',
                component: () =>
                    import(
                        /* webpackChunkName: "transcript-update" */ '@/views/Billing/UpdateTranscript.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['transcripts-update', 'all-root-permission'],
                },
            },
            {
                path: '/billing',
                name: 'Billing',
                component: () =>
                    import(/* webpackChunkName: "billing" */ '@/views/Billing/Billing.vue'),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: 'transcripts',
                        name: 'BillingTranscripts',
                        component: () =>
                            import(
                                /* webpackChunkName: "transcripts" */ '@/views/Billing/Transcripts.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'transcripts/manual',
                        name: 'ManualGeneration',
                        component: () =>
                            import(
                                /* webpackChunkName: "manual-generation" */ '@/views/Billing/ManualGeneration.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'export-transcripts',
                        name: 'BillingExportTranscripts',
                        component: () =>
                            import(
                                /* webpackChunkName: "export-transcripts" */ '@/views/Billing/ExportTranscripts.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'discounts',
                        name: 'BillingDiscounts',
                        component: () =>
                            import(
                                /* webpackChunkName: "discounts" */ '@/views/Billing/Discounts/Discounts.vue'
                            ),
                        children: [
                            {
                                path: '',
                                name: 'BillingDiscountsBusiness',
                                component: () =>
                                    import(
                                        /* webpackChunkName: "business-discounts" */ '@/views/Billing/Discounts/DiscountsBusiness.vue'
                                    ),
                                meta: {
                                    requiresAuth: true,
                                },
                            },
                            {
                                path: 'spark-kz',
                                name: 'BillingDiscountsSparkKz',
                                component: () =>
                                    import(
                                        /* webpackChunkName: "spark-kz-discounts" */ '@/views/Billing/Discounts/DiscountsSparkKz.vue'
                                    ),
                                meta: {
                                    requiresAuth: true,
                                },
                            },
                            {
                                path: 'spark-kz/to-company',
                                name: 'BillingDiscountsSparkKzToCompany',
                                component: () =>
                                    import(
                                        /* webpackChunkName: "spark-kz-discounts-to-company" */ '@/views/Billing/Discounts/DiscountSparkKzToCompany.vue'
                                    ),
                                meta: {
                                    requiresAuth: true,
                                },
                            },
                            {
                                path: 'spark-kz/:discount_id',
                                name: 'BillingDiscountsSparkKzAdditional',
                                component: () =>
                                    import(
                                        /* webpackChunkName: "spark-kz-discounts-additional" */ '@/views/Billing/Discounts/DiscountsSparkKzAdditional.vue'
                                    ),
                                meta: {
                                    requiresAuth: true,
                                },
                            },
                        ],
                    },
                    {
                        path: '',
                        name: 'BillingCalculation',
                        component: () =>
                            import(
                                /* webpackChunkName: "billing-calculation" */ '@/views/Billing/Calculation.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/billing/calculations/update',
                        name: 'UpdateCalculation',
                        component: () =>
                            import(
                                /* webpackChunkName: "update-calculation" */ '@/views/Billing/UpdateCalculation.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/billing/calculations/update-price/:calculation_id',
                        name: 'BillingUpdateCalculationPrice',
                        component: () =>
                            import(
                                /* webpackChunkName: "billing-update-calculation-price" */ '@/views/Billing/UpdateCalculationPrice.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/billing/calculations/:calculation_id',
                        name: 'BillingCalculationId',
                        component: () =>
                            import(
                                /* webpackChunkName: "billing-calculation-id" */ '@/views/Billing/CalculationId.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/billing/calculations/invoice/:invoice_id',
                        name: 'BillingCalculationInvoice',
                        component: () =>
                            import(
                                /* webpackChunkName: "billing-invoice-calculation" */ '@/views/Billing/InvoiceCalculation.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/tariffs',
                        name: 'TariffsHolder',
                        component: () =>
                            import(
                                /* webpackChunkName: "tariffs-holder" */ '@/views/Billing/Tariffs/TariffsHolder.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                        children: [
                            {
                                path: '',
                                name: 'Tariffs',
                                component: () =>
                                    import(
                                        /* webpackChunkName: "tariffs" */ '@/views/Billing/Tariffs/Tariffs.vue'
                                    ),
                                meta: {
                                    requiresAuth: true,
                                },
                                children: [
                                    {
                                        path: '',
                                        name: 'TariffsStandard',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "tariffs-standard" */ '@/views/Billing/Tariffs/TariffsStandard.vue'
                                            ),
                                        meta: {
                                            requiresAuth: true,
                                        },
                                    },
                                    {
                                        path: '/tariffs/individual',
                                        name: 'TariffsIndividual',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "tariffs-individual" */ '@/views/Billing/Tariffs/TariffsIndividual.vue'
                                            ),
                                        meta: {
                                            requiresAuth: true,
                                        },
                                    },
                                    {
                                        path: '/tariffs/individual/:id',
                                        name: 'TariffsIndividualItem',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "tariffs-individual-item" */ '@/views/Billing/Tariffs/TariffsIndividualItem.vue'
                                            ),
                                        meta: {
                                            requiresAuth: true,
                                        },
                                    },
                                    {
                                        path: '/tariffs/ftl/:id',
                                        name: 'TariffsFtlItem',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "tariffs-ftl-item" */ '@/views/Billing/Tariffs/TariffsFtlItem.vue'
                                            ),
                                        meta: {
                                            requiresAuth: true,
                                        },
                                    },
                                    {
                                        path: '/tariffs/unique',
                                        name: 'TariffsUnique',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "tariffs-unique" */ '@/views/Billing/Tariffs/TariffsUnique.vue'
                                            ),
                                        meta: {
                                            requiresAuth: true,
                                        },
                                    },
                                    {
                                        path: '/tariffs/unique/:id',
                                        name: 'TariffsUniqueItem',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "tariffs-unique-item" */ '@/views/Billing/Tariffs/TariffsUniqueItem.vue'
                                            ),
                                        meta: {
                                            requiresAuth: true,
                                        },
                                    },
                                    {
                                        path: 'spark-kz/standart',
                                        name: 'TariffsSparkKzStandart',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "tariffs-spark-kz-standart" */ '@/views/Billing/Tariffs/TariffsStandartSparkKz.vue'
                                            ),
                                        meta: { requiresAuth: true },
                                    },
                                    {
                                        path: 'spark-kz/individual',
                                        name: 'TariffsSparkKzIndividual',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "tariffs-spark-kz-individual" */ '@/views/Billing/Tariffs/TariffsIndividualSparkKz.vue'
                                            ),
                                        meta: { requiresAuth: true },
                                    },
                                    {
                                        path: 'spark-kz/individual/:id',
                                        name: 'TariffsSparkKzIndividualItem',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "tariffs-spark-kz-individual-item" */ '@/views/Billing/Tariffs/TariffsIndividualSparkKzItem.vue'
                                            ),
                                        meta: { requiresAuth: true },
                                    },
                                    {
                                        path: 'spark-kz/individual/:id/update',
                                        name: 'TariffsSparkKzIndividualItemUpdate',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "tariffs-spark-kz-individual-item-update" */ '@/views/Billing/Tariffs/TariffsIndividualSparkKzEdit.vue'
                                            ),
                                        meta: { requiresAuth: true },
                                    },
                                    {
                                        path: 'spark-kz/individual/create',
                                        name: 'TariffsSparkKzIndividualItemCreate',
                                        component: () =>
                                            import(
                                                /* webpackChunkName: "tariffs-spark-kz-individual-item-create" */ '@/views/Billing/Tariffs/TariffsIndividualSparkKzCreate.vue'
                                            ),
                                        meta: { requiresAuth: true },
                                    },
                                ],
                            },
                            {
                                path: '/tariffs/ftl',
                                name: 'TariffsFtl',
                                component: () =>
                                    import(
                                        /* webpackChunkName: "tariffs-ftl" */ '@/views/Billing/Tariffs/TariffsFtl.vue'
                                    ),
                                meta: {
                                    requiresAuth: true,
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: '/rpp',
                name: 'RPP',
                component: () => import(/* webpackChunkName: "rpp" */ '@/views/RPP/RPP.vue'),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'RPPCreate',
                        component: () =>
                            import(
                                /* webpackChunkName: "rpp-create" */ '@/views/RPP/RPPCreate.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/rpp/edit',
                        name: 'RPPEdit',
                        component: () =>
                            import(/* webpackChunkName: "rpp-edit" */ '@/views/RPP/RPPEdit.vue'),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/rpp/ltl',
                        name: 'RPPLtl',
                        component: () =>
                            import(/* webpackChunkName: "rpp-ltl" */ '@/views/RPP/RppLtl.vue'),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/rpp/ltl/:id',
                        name: 'RPPLtlDetails',
                        component: () =>
                            import(
                                /* webpackChunkName: "rpp-ltl-details" */ '@/views/RPP/RppLtlDetails.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/rpp/:id',
                        name: 'RPPItem',
                        component: () =>
                            import(/* webpackChunkName: "rpp-item" */ '@/views/RPP/RPPItem.vue'),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/rpp/edit/:id',
                        name: 'RPPEditItem',
                        component: () =>
                            import(
                                /* webpackChunkName: "rpp-edit-item" */ '@/views/RPP/RPPEditItem.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                ],
            },
            {
                path: '/rpk',
                name: 'RPKHolder',
                component: () =>
                    import(/* webpackChunkName: "rpk-holder" */ '@/views/RPK/RPKHolder.vue'),
                meta: {
                    requiresAuth: true,
                },
                children: [
                    {
                        path: '',
                        name: 'RPK',
                        component: () =>
                            import(/* webpackChunkName: "rpk" */ '@/views/RPK/Main/RPK.vue'),
                        meta: {
                            requiresAuth: true,
                        },
                        children: [],
                    },
                    {
                        path: '/rpk/:id',
                        name: 'RPKItem',
                        component: () =>
                            import(
                                /* webpackChunkName: "rpk-item" */ '@/views/RPK/Main/RPKItem.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/rpk/edit/:id',
                        name: 'RPKEditItem',
                        component: () =>
                            import(
                                /* webpackChunkName: "rpk-edit-item" */ '@/views/RPK/Main/RPKEditItem.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: 'raketa',
                        name: 'RaketaRPK',
                        component: () =>
                            import(
                                /* webpackChunkName: "raketa-rpk" */ '@/views/RPK/Raketa/Raketa.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/rpk/raketa/:id',
                        name: 'RaketaRPKItem',
                        component: () =>
                            import(
                                /* webpackChunkName: "raketa-rpk-item" */ '@/views/RPK/Raketa/RaketaItem.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/rpk/raketa/edit/:id',
                        name: 'RaketaRPKEditItem',
                        component: () =>
                            import(
                                /* webpackChunkName: "raketa-rpk-edit-item" */ '@/views/RPK/Raketa/RaketaEditItem.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                    {
                        path: '/rpk/raketa/create',
                        name: 'RaketaRPKCreate',
                        component: () =>
                            import(
                                /* webpackChunkName: "raketa-rpk-create" */ '@/views/RPK/Raketa/CreateRaketaRPK.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                        },
                    },
                ],
            },
            {
                path: '/rp/:id',
                name: 'RPItem',
                component: () => import(/* webpackChunkName: "rp-item" */ '@/views/RP/RPItem.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/billing/calculations/create',
                name: 'BillingCalculationCreate',
                component: () =>
                    import(
                        /* webpackChunkName: "billing-create-calculation" */ '@/views/Billing/CreateCalculation.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/billing/calculations/international/create',
                name: 'InternationalCalculationCreate',
                component: () =>
                    import(
                        /* webpackChunkName: "international-create-calculation" */ '@/views/Billing/CreateInternationalCalculation.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/billing/tariffs/:tariff_id',
                name: 'BillingTariff',
                component: () =>
                    import(
                        /* webpackChunkName: "billing-tariff" */ '@/views/Billing/Tariffs/BillingTariff.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/billing/tariffs/create-individual',
                name: 'TariffsIndividualCreate',
                component: () =>
                    import(
                        /* webpackChunkName: "tariffs-individual-create" */ '@/views/Billing/Tariffs/TariffsIndividualCreate.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/billing/tariffs/create-ftl',
                name: 'TariffsFtlCreate',
                component: () =>
                    import(
                        /* webpackChunkName: "tariffs-ftl-create" */ '@/views/Billing/Tariffs/TariffsFtlCreate.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/billing/tariffs/edit-individual/:id',
                name: 'TariffsIndividualEdit',
                component: () =>
                    import(
                        /* webpackChunkName: "tariffs-individual-edit" */ '@/views/Billing/Tariffs/TariffsIndividualEdit.vue'
                    ),
                meta: {
                    requiresAuth: true,
                },
            },
        ],
    },
    {
        path: '/',
        component: AuthLayout,
        name: 'AuthLayout',
        children: [
            {
                path: '/auth',
                name: 'Auth',
                component: AuthView,
            },
        ],
    },
    {
        path: '/',
        name: 'A4Landscape',
        component: A4Landscape,
        children: [
            {
                path: '/ttn/:ttn_id/print/:order_id',
                name: 'TTNPrint',
                component: () => import(/* webpackChunkName: "ttn-print" */ '@/views/TTNPrint.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/waybill/:invoice_id',
                name: 'Waybillprint',
                component: () =>
                    import(/* webpackChunkName: "waybill-print" */ '@/views/Waybill.vue'),
                meta: {
                    requiresAuth: true,
                },
            },
        ],
    },
    {
        path: '/',
        name: 'Service',
        component: ServiceLayout,
        children: [
            {
                path: '/ttn',
                name: 'TTN',
                component: () => import(/* webpackChunkName: "ttn" */ '@/views/TTNService/TTN.vue'),
            },
        ],
    },
    {
        path: '/oktell/',
        name: 'Oktell',
        component: OktellLayout,
        children: [
            {
                path: '',
                name: 'OktellIndex',
                component: () =>
                    import(/* webpackChunkName: "oktell-index" */ '@/views/Oktell/Index.vue'),
            },
            {
                path: 'clients',
                name: 'OktellClient',
                component: () =>
                    import(/* webpackChunkName: "oktell-client" */ '@/views/Oktell/Client.vue'),
            },
            {
                path: 'clients/communications',
                name: 'OktellCommunications',
                component: () =>
                    import(
                        /* webpackChunkName: "oktell-communications" */ '@/views/Oktell/Communications.vue'
                    ),
            },
            {
                path: 'clients/invoices/:invoice_id',
                name: 'OktellClientInvoice',
                component: () =>
                    import(
                        /* webpackChunkName: "oktell-client-invoice" */ '@/views/Oktell/Invoice.vue'
                    ),
            },
            {
                path: 'clients/invoices/:invoice_id/edit',
                name: 'OktellClientInvoiceEdit',
                component: () =>
                    import(
                        /* webpackChunkName: "oktell-client-invoice-edit" */ '@/views/Oktell/InvoiceEdit.vue'
                    ),
            },
            {
                path: 'create-order',
                name: 'OktellCreateOrder',
                component: () =>
                    import(
                        /* webpackChunkName: "oktell-create-order" */ '@/views/Oktell/CreateOrder.vue'
                    ),
            },
        ],
    },
    {
        path: '/inventory',
        name: 'InventoryTMS',
        component: NewDefaultLayout,
        children: [
            {
                path: '',
                name: 'InventoryTMS',
                component: () =>
                    import(/* webpackChunkName: "inventory" */ '@/views/TMS/InventoryTMS.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: [],
                },
            },
        ],
        meta: {
            requiresAuth: true,
            permissions: [],
        },
    },
    {
        path: '/tms',
        name: 'TMS',
        component: NewDefaultLayout,
        children: [
            {
                path: 'directions',
                name: 'DirectionsTMS',
                component: () =>
                    import(
                        /* webpackChunkName: "directions-tms" */ '@/views/TMS/DirectionsTMS.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: [],
                },
            },
            {
                path: 'directions/:id',
                name: 'DirectionTMS',
                component: () =>
                    import(/* webpackChunkName: "direction-tms" */ '@/views/TMS/DirectionTMS.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: [],
                },
            },
            {
                path: 'trips',
                name: 'TripsTMS',
                component: () =>
                    import(/* webpackChunkName: "trips-tms" */ '@/views/TMS/TripsTMS.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['tms.trip-index'],
                },
                children: [
                    {
                        path: '',
                        name: 'TripsListTMS',
                        component: () =>
                            import(
                                /* webpackChunkName: "trips-list-tms" */ '@/views/TMS/TripsListTMS.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: [],
                        },
                    },
                    {
                        path: 'create-bid',
                        name: 'CreateBidTMS',
                        component: () =>
                            import(
                                /* webpackChunkName: "create-bid-tms" */ '@/views/TMS/CreateBidTMS.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: [],
                        },
                    },
                    {
                        path: 'ttns',
                        name: 'TtnsListTMS',
                        component: () =>
                            import(
                                /* webpackChunkName: "ttns-list-tms" */ '@/views/TMS/TtnsListTMS.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: ['tms.ttn-index'],
                        },
                    },
                ],
            },
            {
                path: 'trips/:id',
                name: 'TripTMS',
                component: () =>
                    import(/* webpackChunkName: "trip-tms" */ '@/views/TMS/TripTMS.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['tms.trip-index'],
                },
            },
            {
                path: 'ttns/:id',
                name: 'TtnTMS',
                component: () => import(/* webpackChunkName: "ttn-tms" */ '@/views/TMS/TtnTMS.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['tms.ttn-index'],
                },
            },
            {
                path: '/cms/invoices',
                name: 'CmsInvoices',
                component: () =>
                    import(/* webpackChunkName: "cms-invoices" */ '@/views/CMS/Invoices.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: [],
                },
            },
            {
                path: 'graph',
                name: 'GraphTMS',
                component: () =>
                    import(/* webpackChunkName: "tms-graph" */ '@/views/TMS/Graph.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: ['tms.pre-trip-index'],
                },
                children: [
                    {
                        path: '',
                        name: 'AutoGraphTMS',
                        component: () =>
                            import(
                                /* webpackChunkName: "auto-graph-tms" */ '@/views/TMS/GraphAuto.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: [],
                        },
                    },
                    {
                        path: 'express',
                        name: 'AviaGraphTMS',
                        component: () =>
                            import(
                                /* webpackChunkName: "avia-graph-tms" */ '@/views/TMS/GraphAvia.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: [],
                        },
                    },
                ],
            },
            {
                path: 'cost-standards',
                name: 'CostStandardsTMS',
                component: () =>
                    import(
                        /* webpackChunkName: "cost-standards-tms" */ '@/views/TMS/CostStandards.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: [],
                },
            },
            {
                path: 'workplace',
                name: 'WorkplaceTms',
                component: () =>
                    import(/* webpackChunkName: "worlplace" */ '@/views/TMS/WorkplaceTms.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: [],
                },
            },
        ],
    },
    {
        path: '/amo-crm',
        name: 'AmoCrmHolder',
        component: NewDefaultLayout,
        children: [
            {
                path: '',
                name: 'AmoCrm',
                component: () =>
                    import(/* webpackChunkName: "amo-crm" */ '@/views/AmoCrm/AmoCrm.vue'),
                meta: {
                    requiresAuth: true,
                    permissions: [],
                },
                children: [
                    {
                        path: 'tasks',
                        name: 'AmoCrmTasks',
                        component: () =>
                            import(
                                /* webpackChunkName: "amo-crm-tasks" */ '@/views/AmoCrm/AmoCrmTasks.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: [],
                        },
                    },
                    {
                        path: 'customers',
                        name: 'AmoCrmCustomers',
                        component: () =>
                            import(
                                /* webpackChunkName: "amo-crm-customers" */ '@/views/AmoCrm/AmoCrmCustomers.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: [],
                        },
                    },
                    {
                        path: 'whatsapp',
                        name: 'AmoCrmWhatsapp',
                        component: () =>
                            import(
                                /* webpackChunkName: "amo-crm-whatsapp" */ '@/views/AmoCrm/AmoCrmWhatsapp.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: [],
                        },
                    },
                    {
                        path: 'whatsapp/:chat_id',
                        name: 'AmoCrmWhatsappChat',
                        component: () =>
                            import(
                                /* webpackChunkName: "amo-crm-whatsapp-chat" */ '@/views/AmoCrm/AmoCrmWhatsappChat.vue'
                            ),
                        meta: {
                            requiresAuth: true,
                            permissions: [],
                        },
                    },
                ],
            },
        ],
    },
    {
        path: '/new-dispatcher',
        name: 'NewDispatcher',
        component: NewDefaultLayout,
        children: [
            {
                path: 'monitoring',
                name: 'NewDispatcherMonitoring',
                component: () =>
                    import(
                        /* webpackChunkName: "new-dispatcher-monitoring" */ '@/views/NewDispatcher/Monitoring.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: [],
                },
            },
            {
                path: 'route-sheet',
                name: 'NewDispatcherRouteSheet',
                component: () =>
                    import(
                        /* webpackChunkName: "new-dispatcher-route-sheet" */ '@/views/NewDispatcher/RouteSheet.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['dispatcher.route-sheet-index', 'all-root-permission'],
                },
            },
            {
                path: 'route-sheet/:id',
                name: 'NewDispatcherRouteSheetDetails',
                component: () =>
                    import(
                        /* webpackChunkName: "new-dispatcher-route-sheet-details" */ '@/views/NewDispatcher/RouteSheetDetails.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['dispatcher.route-sheet-index', 'all-root-permission'],
                },
            },
            {
                path: 'complete-day',
                name: 'NewDispatcherCompleteDay',
                component: () =>
                    import(
                        /* webpackChunkName: "new-dispatcher-complete-day" */ '@/views/NewDispatcher/CompleteDay.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['dispatcher.courier-report', 'all-root-permission'],
                },
            },
            {
                path: 'complete-day/:courier_id',
                name: 'NewDispatcherCompleteDayMore',
                component: () =>
                    import(
                        /* webpackChunkName: "new-dispatcher-complete-day-more" */ '@/views/NewDispatcher/CompleteDayMore.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['dispatcher.courier-report', 'all-root-permission'],
                },
            },
            {
                path: 'planning',
                name: 'NewDisptcherPlanning',
                component: () =>
                    import(
                        /* webpackChunkName: "new-dispatcher-planning" */ '@/views/NewDispatcher/Planning.vue'
                    ),
                meta: {
                    requiresAuth: 'true',
                    permissions: ['dispatcher.planning-index', 'all-root-permission'],
                },
            },
            {
                path: 'forecasting',
                name: 'NewDisptcherForecasting',
                component: () =>
                    import(
                        /* webpackChunkName: "new-dispatcher-forecasting" */ '@/views/NewDispatcher/Forecasting.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['dispatcher.wave-index', 'all-root-permission'],
                },
            },
            {
                path: 'waves/create',
                name: 'NewDisptcherCreateWave',
                component: () =>
                    import(
                        /* webpackChunkName: "new-dispatcher-create-wave" */ '@/views/NewDispatcher/CreateWave.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['dispatcher.wave-store', 'all-root-permission'],
                },
            },
            {
                path: 'waves/:wave_id/update',
                name: 'NewDisptcherUpdateWave',
                component: () =>
                    import(
                        /* webpackChunkName: "new-dispatcher-update-wave" */ '@/views/NewDispatcher/UpdateWave.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['dispatcher.wave-update', 'all-root-permission'],
                },
            },
            {
                path: 'sectors-control',
                name: 'NewDispatcherSectorsControl',
                component: () =>
                    import(
                        /* webpackChunkName: "new-dispatcher-sectors-control" */ '@/views/NewDispatcher/SectorsControl.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: [
                        'dispatcher.sector-index',
                        'dispatcher.dispatcher-sectors-index',
                        'all-root-permission',
                    ],
                },
            },
            {
                path: 'couriers',
                name: 'NewDispatcherCouriers',
                component: () =>
                    import(
                        /* webpackChunkName: "new-dispatcher-couriers" */ '@/views/NewDispatcher/Couriers.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['dispatcher.courier-index', 'all-root-permission'],
                },
            },
            {
                path: 'couriers/:courier_id',
                name: 'NewDispacherCourierDetails',
                component: () =>
                    import(
                        /* webpackChunkName: "new-dispatcher-courier-details" */ '@/views/NewDispatcher/CourierId.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['dispatcher.courier-index', 'all-root-permission'],
                },
            },
            {
                path: 'containers',
                name: 'NewDispatcherContainers',
                component: () =>
                    import(
                        /* webpackChunkName: "new-dispatcher-containers" */ '@/views/NewDispatcher/Containers.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: [],
                },
            },
            {
                path: 'takes',
                name: 'NewDispatcherTakes',
                component: () =>
                    import(
                        /* webpackChunkName: "new-dispatcher-takes" */ '@/views/NewDispatcher/Takes.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['dispatcher.order-take-index', 'all-root-permission'],
                },
            },
            {
                path: 'deliveries',
                name: 'NewDispatcherDeliveries',
                component: () =>
                    import(
                        /* webpackChunkName: "new-dispatcher-deliveries" */ '@/views/NewDispatcher/Deliveries.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['dispatcher.delivery-index', 'all-root-permission'],
                },
            },
        ],
    },
    {
        path: '/dispatcher-workplace',
        component: () =>
            import(
                /* webpackChunkName: "dispatcher-workplace-layout" */ '@/layouts/DispatcherWorkplace.vue'
            ),
        children: [
            {
                path: '',
                name: 'DispatcherWorkplace',
                component: () =>
                    import(
                        /* webpackChunkName: "dispatcher-workplace" */ '@/views/DispatcherWorkplace.vue'
                    ),
                meta: {
                    requiresAuth: true,
                    permissions: ['dispatcher.workplace-index', 'all-root-permission'],
                }
            },
        ],
    },
]

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
})

router.beforeEach(async (to, from, next) => {
    const authorization = useAuth()
    authorization.initialize()

    if (to.meta.requiresAuth) {
        if (!authorization.authenticated.value) {
            next({ name: 'Auth' })
            return
        } else {
            const account = useAccount()
            await account.initialize()

            const roles = to.meta.roles as number[] | undefined
            const permissions = to.meta.permissions as string[] | undefined

            const is_inactive = account.isAccountInactive()
            const is_roles_guard_passed = account.haveRoles(roles)
            const is_permissions_guard_passed = account.havePermissions(permissions)

            if (is_inactive || !is_roles_guard_passed || !is_permissions_guard_passed) {
                if (!from.name) {
                    next({ name: 'Auth' })
                    return
                } else {
                    createToast({ title: 'У вас нет доступа к этой странице' }, { type: 'danger' })
                    return
                }
            } else {
                next()
            }
        }
    } else {
        next()
    }
})

export default router
